import type { ITenantThemeConfig } from '@/shared/api/types/tenant-types'
import { useTranslation } from 'react-i18next'
import { TENANT_THEME_DEFAULT_COLORS, UI_THEME_COLORS } from '@/shared/constants/theme-colors'

interface TenantThemePreviewProps {
  theme: ITenantThemeConfig
}

const PREVIEW_SCALE = 0.85

export function TenantThemePreview({ theme }: TenantThemePreviewProps) {
  const { t } = useTranslation()

  const primaryColor = theme.primaryColor ?? TENANT_THEME_DEFAULT_COLORS.primary
  const secondaryColor = theme.secondaryColor ?? TENANT_THEME_DEFAULT_COLORS.secondary
  const accentColor = theme.accentColor ?? TENANT_THEME_DEFAULT_COLORS.accent
  const fontFamily = theme.fontFamily ?? 'Inter, sans-serif'
  const previewMenuItems = [t('sidebar.dashboard'), t('sidebar.tenants'), t('sidebar.tours')]

  return (
    <div>
      <p className="text-xs text-zinc-400 mb-2">{t('tenant.theme.preview')}</p>

      <div
        style={{
          border: `1px solid ${UI_THEME_COLORS.borderNeutral}`,
          borderRadius: 8,
          overflow: 'hidden',
          transform: `scale(${PREVIEW_SCALE})`,
          transformOrigin: 'top left',
          width: `${100 / PREVIEW_SCALE}%`,
          fontFamily,
        }}
      >
        {/* Header bar */}
        <div
          style={{
            background: secondaryColor,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {theme.logoUrl ? (
            <img src={theme.logoUrl} alt="logo" style={{ height: 24, objectFit: 'contain' }} />
          ) : (
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 4,
                background: UI_THEME_COLORS.whiteAlpha40,
              }}
            />
          )}
          <span style={{ color: UI_THEME_COLORS.white, fontWeight: 600, fontSize: 13 }}>
            Admin Panel
          </span>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', background: UI_THEME_COLORS.neutral100, minHeight: 160 }}>
          {/* Sidebar */}
          <div
            style={{
              width: 80,
              background: primaryColor,
              padding: '12px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {previewMenuItems.map((item) => (
              <div
                key={item}
                style={{
                  background: UI_THEME_COLORS.whiteAlpha15,
                  borderRadius: 4,
                  padding: '4px 6px',
                  color: UI_THEME_COLORS.white,
                  fontSize: 10,
                  cursor: 'default',
                }}
              >
                {item}
              </div>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Card */}
            <div
              style={{
                background: UI_THEME_COLORS.white,
                borderRadius: 6,
                border: `2px solid ${accentColor}`,
                padding: '8px 12px',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: UI_THEME_COLORS.textStrong,
                  fontFamily,
                }}
              >
                Sample Card
              </div>
              <div
                style={{ fontSize: 10, color: UI_THEME_COLORS.textMuted, marginTop: 4, fontFamily }}
              >
                Content area with accent border
              </div>
            </div>

            {/* Button row */}
            <div style={{ display: 'flex', gap: 6 }}>
              <div
                style={{
                  background: primaryColor,
                  color: UI_THEME_COLORS.white,
                  borderRadius: 4,
                  padding: '4px 10px',
                  fontSize: 10,
                  fontFamily,
                }}
              >
                Primary
              </div>
              <div
                style={{
                  background: secondaryColor,
                  color: UI_THEME_COLORS.white,
                  borderRadius: 4,
                  padding: '4px 10px',
                  fontSize: 10,
                  fontFamily,
                }}
              >
                Secondary
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
